import { DefectiveReturnItemPayload, SparingRequestLineItemPayload } from 'models/SparingRequests';

/**
 * A single returnable unit. The defective returns table renders one row per unit, so a line item
 * with a quantity of three produces three units.
 */
export type DefectiveReturnUnit = {
  line: SparingRequestLineItemPayload;
  lineIndex: number;
  rowKey: string;
  unitIndex: number;
};

/**
 * Expands the cart line items into the one-row-per-unit shape the defective returns table renders.
 * The rowKey includes the line position so that two lines for the same product never collide.
 */
export const expandLineItemsToDefectiveReturnUnits = (lineItems: SparingRequestLineItemPayload[]): DefectiveReturnUnit[] => {
  const units: DefectiveReturnUnit[] = [];

  (lineItems ?? []).forEach((line, lineIndex) => {
    const quantity = line?.quantity ?? 0;

    for (let unitIndex = 0; unitIndex < quantity; unitIndex++) {
      units.push({
        line,
        lineIndex,
        rowKey: `${lineIndex}:${line.productNumber}#${unitIndex}`,
        unitIndex
      });
    }
  });

  return units;
};

/**
 * Builds an untouched defective return item for a single unit. "Didn't Use" is unchecked by
 * default, which means the unit is used. Field for field this is the default row main builds.
 */
export const buildDefaultDefectiveReturnItem = (unit: DefectiveReturnUnit): DefectiveReturnItemPayload => ({
  lineNumber: unit.line.lineNumber,
  productNumber: unit.line.productNumber,
  serialNumber: '',
  caseNumber: '',
  rmaNumber: '',
  quantity: 1,
  used: true
});

/**
 * A defective return item as it lives in formik while the MDSI1-4304 requirement is active.
 *
 * matchedProductNumber is CLIENT-ONLY. It records which cart product the row was matched to, because
 * the item's own productNumber is the user-editable "Return Part Number" and cannot be trusted as an
 * identity once the user has typed into it. Every submit path builds its items field by field, so
 * this marker never reaches the API.
 */
export type TrackedDefectiveReturnItem = DefectiveReturnItemPayload & { matchedProductNumber?: string };

/** A current unit and the stored defective return item that belongs to it, if any. */
export type DefectiveReturnUnitMatch = {
  item?: DefectiveReturnItemPayload;
  unit: DefectiveReturnUnit;
};

/** The queue key for the items owned by one cart line - see getOwnerKey. */
const lineOwnerKey = (lineIndex: number): string => `line:${lineIndex}`;

/** The queue key for the items owned by a cart product rather than by one line - see getOwnerKey. */
const productOwnerKey = (productNumber: string): string => `product:${productNumber}`;

/**
 * Works out who a stored item belongs to, as a queue key:
 *  1. the client-only marker, when the table has already matched the item - owned by that product;
 *  2. otherwise its lineNumber, when that is above zero and names a line currently in the cart -
 *     owned by that LINE. This is an item fresh from the API (or from a formik reinitialise): its
 *     lineNumber is reliable, because Edit numbers its line items idx + 1, the Products step keeps an
 *     existing line's lineNumber when it re-orders the cart (it spreads the line), and a line added
 *     since is numbered 0 so it can never be mistaken for a saved one. The lineNumber comes before
 *     the productNumber because the productNumber is the user-editable "Return Part Number": a saved
 *     item whose Return Part Number was changed to another product in the cart still belongs to its
 *     own line;
 *  3. otherwise its productNumber, when that is a product in the cart - owned by that product. This
 *     covers Create items (numbered 0) and saved items whose line is no longer in the cart;
 *  4. otherwise nothing, and the item is dropped: there is no unit left for it to belong to.
 */
const getOwnerKey = (item: DefectiveReturnItemPayload, lineItems: SparingRequestLineItemPayload[], cartProductNumbers: Set<string>): string | undefined => {
  const { matchedProductNumber } = item as TrackedDefectiveReturnItem;

  if (matchedProductNumber) return productOwnerKey(matchedProductNumber);

  if (item.lineNumber > 0) {
    const lineIndex = lineItems.findIndex((line) => line.lineNumber === item.lineNumber);

    if (lineIndex >= 0) return lineOwnerKey(lineIndex);
  }

  if (cartProductNumbers.has(item.productNumber)) return productOwnerKey(item.productNumber);

  return undefined;
};

/**
 * Matches stored defective return items to the current units by IDENTITY, never by position. An
 * item owned by a line (see getOwnerKey) goes to that line's units, in order; an item owned by a
 * product goes, in order, to the units of that product that no line-owned item has taken. That holds
 * across a remount of the table, across the Products step re-ordering the cart (adding more of a
 * product moves its line to the end), across a quantity increase (the new units get no item) and
 * across a quantity decrease or product removal (surplus items are dropped).
 *
 * Pure - the result is one entry per unit, in unit order.
 */
export const matchDefectiveReturnItemsToUnits = (items: DefectiveReturnItemPayload[] | undefined, lineItems: SparingRequestLineItemPayload[] | undefined): DefectiveReturnUnitMatch[] => {
  const safeLineItems = lineItems ?? [];
  const units = expandLineItemsToDefectiveReturnUnits(safeLineItems);
  const cartProductNumbers = new Set(safeLineItems.map((line) => line.productNumber));
  const queues = new Map<string, DefectiveReturnItemPayload[]>();

  (items ?? []).forEach((item) => {
    if (!item) return;

    const owner = getOwnerKey(item, safeLineItems, cartProductNumbers);

    if (owner === undefined) return;

    const queue = queues.get(owner) ?? [];

    queue.push(item);
    queues.set(owner, queue);
  });

  return units.map((unit) => ({
    unit,
    item: queues.get(lineOwnerKey(unit.lineIndex))?.shift() ?? queues.get(productOwnerKey(unit.line.productNumber))?.shift()
  }));
};

/**
 * The defectiveReturns.items array the table should hold while the requirement is active: one
 * item per unit in unit order, each marked with the product it was matched to. An item that is
 * already in that shape is returned as the same object, so a caller can compare by reference and
 * skip a no-op write.
 */
export const alignDefectiveReturnItemsToUnits = (items: DefectiveReturnItemPayload[] | undefined, lineItems: SparingRequestLineItemPayload[] | undefined): TrackedDefectiveReturnItem[] =>
  matchDefectiveReturnItemsToUnits(items, lineItems).map(({ item, unit }) => {
    const matchedProductNumber = unit.line.productNumber;

    if (!item) return { ...buildDefaultDefectiveReturnItem(unit), matchedProductNumber };

    const tracked = item as TrackedDefectiveReturnItem;

    if (tracked.quantity === 1 && tracked.matchedProductNumber === matchedProductNumber) return tracked;

    return { ...tracked, quantity: 1, matchedProductNumber };
  });

/**
 * The defective return items to SUBMIT while the requirement is active. Each item carries the line
 * number of the unit it was matched to - the layouts number the submitted line items idx + 1, so
 * lineIndex + 1 is that same number - and a unit with no item gets a default one. Fields are copied
 * one by one, so the client-only marker is left behind.
 *
 * Only for the requirement-active path. With the requirement off both layouts build their items
 * exactly as main does, and line numbers pass through untouched.
 */
export const renumberDefectiveReturnItems = (items: DefectiveReturnItemPayload[] | undefined, lineItems: SparingRequestLineItemPayload[] | undefined): DefectiveReturnItemPayload[] =>
  matchDefectiveReturnItemsToUnits(items, lineItems).map(({ item, unit }) => {
    const source = item ?? buildDefaultDefectiveReturnItem(unit);

    return {
      lineNumber: unit.lineIndex + 1,
      productNumber: source.productNumber,
      serialNumber: source.serialNumber ?? '',
      quantity: 1,
      caseNumber: source.caseNumber,
      rmaNumber: source.rmaNumber,
      used: source.used ?? true,
      notes: source.notes
    };
  });

/* ***************************************************************************************
 * MDSI1-4304 - "Require Defective Item Information".
 *
 * PROVISIONAL, pending MDSI1-4304 sign-off. Every rule below is a product decision that has
 * not been signed off yet, so all of them live in this one block and nowhere else - change
 * them here and every consumer (the table, the Post button, both layouts' validate, the step
 * icon) follows:
 *
 *  (a) The requirement is driven by the EXPECTED row count - the sum of the line item
 *      quantities - and NOT by defectiveReturns.items.length. An expected row with no backing
 *      item is an error, because a short items array is exactly the case where the user never
 *      filled anything in. NOTE the API is deliberately more lenient: it allows a submit when
 *      the items array is empty or absent. That gap between client and server is a known open
 *      question on the story, not a bug to fix here.
 *  (b) A row marked "Didn't Use" (used === false) is exempt from the requirement.
 *  (c) When zero rows are expected there is nothing to require, and the request submits.
 *  (d) isDefectiveReturnsInformationRequired on its own does nothing. The requirement is inert
 *      unless isActivateDefectiveReturns is also true - no error, no coercion, no gating of the
 *      admin toggle - matching the isEndCustomerVisible / isEndCustomerVisibleRequired pair.
 * *************************************************************************************** */

/**
 * The fields a defective return row must carry when the requirement is active, with the labels
 * the user sees. These labels are the only names for these two fields anywhere in the app.
 */
export const DEFECTIVE_RETURN_REQUIRED_FIELDS = [
  { key: 'productNumber', label: 'Return Part Number' },
  { key: 'serialNumber', label: 'Return Serial Number' }
] as const;

/** A field of a defective return item that the requirement covers. */
export type DefectiveReturnRequiredFieldKey = typeof DEFECTIVE_RETURN_REQUIRED_FIELDS[number]['key'];

/** The required-field errors of a single defective return row, keyed by field. */
export type DefectiveReturnRowErrors = Partial<Record<DefectiveReturnRequiredFieldKey, string>>;

/** The verdict of the requirement over a whole request. */
export type DefectiveReturnsRequiredInfoResult = {
  /** The number of rows the requirement expects, per rule (a). Zero when the rule is inert. */
  expectedRowCount: number;
  /** The 1-based numbers of the rows that are missing something, for user-facing messages. */
  invalidRowNumbers: number[];
  /** Whether both flags are on, per rule (d). Everything else is inert when this is false. */
  isRequirementActive: boolean;
  /** Whether the request may be posted as far as this requirement is concerned. */
  isValid: boolean;
  /** A single user-facing sentence naming the missing fields and rows, or undefined when valid. */
  message?: string;
  /** The distinct labels of the fields that are missing somewhere, in field order. */
  missingFieldLabels: string[];
  /** The same thing phrased as one entry of a "following required fields" list, or undefined. */
  missingFieldsSummary?: string;
  /** Per-row errors, positional and always expectedRowCount long, so a table can render them. */
  rowErrors: DefectiveReturnRowErrors[];
};

/** The inputs the requirement is computed from. */
export type DefectiveReturnsRequiredInfoArgs = {
  items?: DefectiveReturnItemPayload[];
  lineItems?: SparingRequestLineItemPayload[];
  requestType?: DefectiveReturnsRequirementFlags | null;
};

/** The two sparing request type flags that together switch the requirement on. */
export type DefectiveReturnsRequirementFlags = {
  isActivateDefectiveReturns?: boolean;
  isDefectiveReturnsInformationRequired?: boolean;
};

/**
 * Whether the requirement applies at all, per provisional rule (d). Both flags must be true;
 * isDefectiveReturnsInformationRequired on its own is ignored.
 */
export const isDefectiveReturnsInformationRequirementActive = (requestType?: DefectiveReturnsRequirementFlags | null): boolean =>
  Boolean(requestType?.isActivateDefectiveReturns && requestType?.isDefectiveReturnsInformationRequired);

/**
 * Joins user-facing names into a readable list - "A", "A and B", "A, B and C".
 */
const joinReadableList = (parts: string[]): string => {
  if (parts.length <= 1) return parts[0] ?? '';

  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

/**
 * The one place that answers "which defective return rows are missing required information?".
 * Every consumer - the defective returns table, the Post button, both layouts' formik validate
 * and the step icon - calls this, so none of them can disagree with each other.
 */
export const getDefectiveReturnsRequiredInfoErrors = ({ items, lineItems, requestType }: DefectiveReturnsRequiredInfoArgs): DefectiveReturnsRequiredInfoResult => {
  const isRequirementActive = isDefectiveReturnsInformationRequirementActive(requestType);
  // Rule (a): the expected rows are the units the table renders, not the items that exist.
  // Items are matched to rows by product identity - the same matching the table and the submit use -
  // so a stale or re-ordered items array is judged exactly as it will be sent.
  const matches = isRequirementActive ? matchDefectiveReturnItemsToUnits(items, lineItems) : [];
  const expectedRowCount = matches.length;

  // Rule (c): nothing expected, nothing required.
  if (!isRequirementActive || expectedRowCount === 0) {
    return { expectedRowCount, invalidRowNumbers: [], isRequirementActive, isValid: true, missingFieldLabels: [], rowErrors: [] };
  }

  const invalidRowNumbers: number[] = [];
  const missingFieldLabels: string[] = [];
  const rowErrors: DefectiveReturnRowErrors[] = [];

  for (let rowIndex = 0; rowIndex < expectedRowCount; rowIndex++) {
    const { item } = matches[rowIndex];
    const errors: DefectiveReturnRowErrors = {};

    // Rule (b): "Didn't Use" rows are exempt.
    if (item?.used === false) {
      rowErrors.push(errors);
      continue;
    }

    DEFECTIVE_RETURN_REQUIRED_FIELDS.forEach(({ key, label }) => {
      // Rule (a): a missing item at an expected row counts exactly like a blank field.
      if ((item?.[key] ?? '').trim()) return;

      errors[key] = `${label} is required`;
      if (!missingFieldLabels.includes(label)) missingFieldLabels.push(label);
    });

    if (Object.keys(errors).length) invalidRowNumbers.push(rowIndex + 1);
    rowErrors.push(errors);
  }

  const isValid = invalidRowNumbers.length === 0;
  const rowsPhrase = `Defective Returns row${invalidRowNumbers.length === 1 ? '' : 's'} ${invalidRowNumbers.join(', ')}`;

  return {
    expectedRowCount,
    invalidRowNumbers,
    isRequirementActive,
    isValid,
    message: isValid ? undefined : `${joinReadableList(missingFieldLabels)} ${missingFieldLabels.length === 1 ? 'is' : 'are'} required on ${rowsPhrase}`,
    missingFieldLabels,
    missingFieldsSummary: isValid ? undefined : `${joinReadableList(missingFieldLabels)} on ${rowsPhrase}`,
    rowErrors
  };
};
